import type {
	JiraFormattingNoValueOperator,
	JiraFormattingSingleValueOperator,
	JiraFormattingMultipleValueOperator,
} from '@atlassian/jira-relay/src/__generated__/useSaveRuleOldMutation.graphql';

export type FormattingRuleOperators =
	| JiraFormattingNoValueOperator
	| JiraFormattingSingleValueOperator
	| JiraFormattingMultipleValueOperator;

type NumberValue = number;
type StringValue = string;
export type DateValue = Date;
export type FieldValuePrimitive = DateValue | StringValue | NumberValue | undefined | null;
export type FieldValue = FieldValuePrimitive | string[];
export type DateRuleValue =
	| 'EXACT_DATE'
	| 'TODAY'
	| 'TOMORROW'
	| 'YESTERDAY'
	| 'THIS_WEEK'
	| 'LAST_WEEK'
	| 'NEXT_WEEK'
	| 'THIS_QUARTER'
	| 'LAST_QUARTER'
	| 'NEXT_QUARTER'
	| 'THIS_YEAR'
	| 'LAST_YEAR'
	| 'NEXT_YEAR';

export type RuleValuePrimitive = DateRuleValue | StringValue;
export type RuleValue =
	| undefined
	| RuleValuePrimitive
	| RuleValuePrimitive[]
	| readonly RuleValuePrimitive[];

const dateRuleValue = new Set([
	'EXACT_DATE',
	'LAST_QUARTER',
	'LAST_WEEK',
	'LAST_YEAR',
	'NEXT_QUARTER',
	'NEXT_WEEK',
	'NEXT_YEAR',
	'THIS_QUARTER',
	'THIS_WEEK',
	'THIS_YEAR',
	'TODAY',
	'TOMORROW',
	'YESTERDAY',
]);
export const isDateRuleValue = (value: RuleValuePrimitive): value is DateRuleValue => {
	return dateRuleValue.has(value);
};

const noValueOperators = new Set(['IS_EMPTY', 'IS_NOT_EMPTY']);
export const isNoValueOperator = (
	operator: FormattingRuleOperators,
): operator is JiraFormattingNoValueOperator => {
	return noValueOperators.has(operator);
};

const singleValueOperators = new Set([
	'CONTAINS',
	'DOES_NOT_CONTAIN',
	'DOES_NOT_EQUAL',
	'EQUALS',
	'GREATER_THAN',
	'GREATER_THAN_OR_EQUALS',
	'IS',
	'IS_AFTER',
	'IS_BEFORE',
	'IS_NOT',
	'IS_ON_OR_AFTER',
	'IS_ON_OR_BEFORE',
	'LESS_THAN',
	'LESS_THAN_OR_EQUALS',
]);
export const isSingleValueOperator = (
	operator: FormattingRuleOperators,
): operator is JiraFormattingSingleValueOperator => {
	return singleValueOperators.has(operator);
};

export type DateRuleOnlyOperator =
	| 'IS'
	| 'IS_AFTER'
	| 'IS_BEFORE'
	| 'IS_NOT'
	| 'IS_ON_OR_AFTER'
	| 'IS_ON_OR_BEFORE';
const dateRuleOnlyOperator = new Set([
	'IS',
	'IS_AFTER',
	'IS_BEFORE',
	'IS_NOT',
	'IS_ON_OR_AFTER',
	'IS_ON_OR_BEFORE',
]);

export const isDateRuleOnlyOperator = (
	operator: JiraFormattingSingleValueOperator,
): operator is DateRuleOnlyOperator => {
	return dateRuleOnlyOperator.has(operator);
};

const multipleValueOperators = new Set([
	'CONTAINS',
	'DOES_NOT_CONTAIN',
	'HAS_ANY_OF',
	'IS_BETWEEN',
]);
export const isMultipleValueOperator = (
	operator: FormattingRuleOperators,
): operator is JiraFormattingMultipleValueOperator => {
	return multipleValueOperators.has(operator);
};

export const isString = (maybeString: unknown): maybeString is string => {
	return typeof maybeString === 'string';
};

export const isNumber = (maybeNumber: unknown): maybeNumber is number => {
	return typeof maybeNumber === 'number';
};
